import { gql } from "graphql-tag";

/** Fetches ticketGroups with their ProductSlotCategories, including Seating Plan data */
export const GET_TicketGroupsTicketsPCsWithSeatingPlanData = gql`
  query GetTicketGroupsTicketsPCsWithSeatingPlanData(
    $ticketGroups: String!
    $skip: Int
    $limit: Int
  ) {
    productsCategories: TicketGroupTicketList(
      where: $ticketGroups
      limit: $limit
      offset: $skip
    ) {
      productCategory: ProductSlotCategoryRead {
        id: Id
        cancelled: Cancelled
        name: CategoryName
        description: CategoryDescription
        type: Type
        address: Address
        qrCodeType: QrCodeType
        multipleScan: MultipleScan
        categoryInfoList: CategoryInfoList {
          scanOpeningMinutesBeforeStart: ScanOpeningMinutesBeforeStart
          __typename
        }
        sortOrderPos: SortOrderPos
        timeSlotCount: TimeSlotCount

        # used for seating plan logic
        resource: ResourceRead {
          quantity: Quantity
          __typename
        }

        saleStartType: SaleStartType
        saleEndType: SaleEndType
        saleStart: SaleStart
        saleEnd: SaleEnd
        salesStartDurationDays: SalesStartDurationDays
        salesEndDurationMinutes: SalesEndDurationMinutes

        teamSettingList: TeamSettingList {
          maxParticipants: MaxParticipants
          categoryId: CategoryId
          countedPsp: CountedPsp
          __typename
        }

        memberships: MembershipList {
          id: Id
          startDate: StartDate
          expiryDate: ExpiryDate
          type: Type
          expiryType: ExpiryType
          periodLength: PeriodLength
          numberMandatoryPeriod: NumberMandatoryPeriod
          __typename
        }

        promoCodeCampaignList: PromoCodeCampaignList {
          id: Id
          giftCardTicketId: GiftCardTicketId
          startDateTime: StartDateTime
          endDateTime: EndDateTime
          expiryType: ExpiryType
          expiryDays: ExpiryDays
          cancelled: Cancelled
          __typename
        }

        stockCategoryList: StockCategoryList {
          id: Id
          categoryId: CategoryId
          type: Type
          quantity: Quantity
          holdStock: HoldStock
          stockRead: StockRead {
            id: Id
            __typename
          }
          __typename
        }

        ticketGroups: TicketGroupTicketList {
          ticketGroupId: TicketGroupId
          __typename
        }
        translations: ProductSlotCategoryTranslationList {
          content: Content
          field: Field
          locale: Locale
          __typename
        }
        product: ProductRead {
          currency: Currency
          name: Name
          id: Id
          isIdivTax: IsIdivTax
          status: Status
          translations: ProductTranslationList {
            content: Content
            field: Field
            locale: Locale
            __typename
          }
          pictures: ProductPictureList {
            picture: PictureRead {
              name: Name
              isPrimary: IsPrimary
              realUrl: RealUrl
              __typename
            }
            __typename
          }
          productBrandingList: ProductBrandingList {
            id: Id
            description1: Description1
            description2: Description2
            description3: Description3
            customText: CustomText
            __typename
          }
          customTicketFieldsList: CustomTicketFieldList {
            id: Id
            productId: ProductId
            questionType: QuestionType
            name: Name
            list: List
            validation: Validation
            informationType: InformationType
            answerExample: AnswerExample
            position: Position
            canceled: Canceled
            visibleFor: VisibleFor
            prices: CustomTicketFieldPriceList {
              priceId: ProductSlotPriceId
              __typename
            }
            __typename
          }
          customBookingFieldsList: CustomBookingFieldList {
            id: Id
            productId: ProductId
            questionType: QuestionType
            name: Name
            list: List
            validation: Validation
            informationType: InformationType
            answerExample: AnswerExample
            position: Position
            canceled: Canceled
            visibleFor: VisibleFor
            __typename
          }
          GroupRead {
            groupUuid: Uuid
            __typename
          }
          __typename
        }
        prices: ProductSlotPriceList {
          id: Id
          name: PriceName
          description: Description
          availability: Availability
          cancelled: Cancelled
          isEditable: IsEditable
          priceCategory: PriceCategory
          sortPrice: SortPrice
          translations: ProductSlotPriceTranslationList {
            content: Content
            field: Field
            locale: Locale
            __typename
          }
          value: Value
          priceOnsite: PriceOnsite
          quantity: QuantitAvailable
          totalQuantity: Quantity
          vatRateGeneralId: VatRateGeneralId
          tax: TaxRead {
            id: Id
            name: Name
            includedInPrice: IncludedInPrice
            rateMiliValue: RateMiliValue
            __typename
          }
          addons: PackagePriceList {
            id: Id
            priceId: PriceId
            automaticallyBook: AutomaticallyBook
            canceled: Canceled
            isMandatory: IsMandatory
            priority: Priority
            sendTicket: SendTicket
            displayedValue: DisplayedValue
            accountingValue: AccountingValue
            minutesBefore: MinutesBefore
            minutesAfter: MinutesAfter
            minutesBeforeStartMin: MinutesBeforeStartMin
            minutesBeforeStartMax: MinutesBeforeStartMax
            minutesAfterStartMin: MinutesAfterStartMin
            minutesAfterStartMax: MinutesAfterStartMax
            maxQuantity: MaxQuantity
            minQuantity: MinQuantity
            displayPage: DisplayPage
            __typename
          }
          updatedAt: UpdatedAt
          retailOptions: RetailOptionList {
            id: Id
            name: OptionName
            nameLocale: OptionNameLocale
            type: OptionType
            inventoryAlert: InventoryAlert
            barcode: Barcode
            sku: Sku
            sellingSoldOut: SellingSoldOut
            smeetzPictures: SmeetzPictureList {
              id: Id
              metaData: MetaData
              name: Name
              entity: Entity
              entityId: EntityId
              __typename
            }
            deletedAt: DeletedAt
            optionSort: OptionSort
            __typename
          }
          packageMinQuantity: PackageMinQuantity
          packageMaxQuantity: PackageMaxQuantity
          __typename
        }
        seatingParentPSCId: SeatingMainSubProductId
        seatsCat: SeatsCat
        __typename
      }
    }
  }
`;

export const COUNT_TicketGroupsTicketsPCs = gql`
  query CountTicketGroupsTicketsPCs($ticketGroups: String!) {
    productsCategories: TicketGroupTicketCount(where: $ticketGroups)
  }
`;

export const GET_ProductCategoriesIdsByPriceIds = gql`
  query getProductCategoriesIdsByPriceIds(
    $prices: String!
    $skip: Int
    $limit: Int
  ) {
    productSlotPrices: ProductSlotPriceList(
      where: $prices
      limit: $limit
      offset: $skip
    ) {
      productCategory: ProductSlotCategoryRead {
        id: Id
        ProductRead {
          GroupRead {
            groupUuid: Uuid
          }
        }
      }
    }
  }
`;
